import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Field, Form, Input, Select, TextArea, Typography } from '@/components';
import { SolarProjectTechnology } from '@/features/projects/types';
import { getDateFromQuarterAndYear, getQuarterFromDate, getYearFromDate, nullableStringSetter } from '@/helpers';
import { usePanelConfiguration, useStructureType } from '@/features/projects/hooks';
import { useDevelopmentStatus } from '@/hooks';
import { NUMERIC } from '@/features/projects/helpers/numeric-inputs';

export type SolarFormFields = {
  development_status: string | null;
  installed_capacity_peak: number | null;
  installed_capacity_nominal: number | null;
  ready_to_build: any;
  ready_to_build_quarter: string | null;
  ready_to_build_year: number | null;
  commercial_operation_date: any;
  commercial_operation_date_quarter: string | null;
  commercial_operation_date_year: number | null;
  specific_production: number | null;
  comments: string | null;
  structure_type: string | null;
  module_type: string | null;
  module_power: number | null;
  panel_configuration: string | null;
  pitch_distance: number | null;
};

export type SolarSubmitFields = {
  development_status: string | null;
  installed_capacity_peak: number | null;
  installed_capacity_nominal: number | null;
  ready_to_build: string | null;
  commercial_operation_date: string | null;
  specific_production: number | null;
  comments: string | null;
  structure_type: string | null;
  module_type: string | null;
  module_power: number | null;
  panel_configuration: string | null;
  pitch_distance: number | null;
};

export type SolarFormProps = {
  readonly id: string;
  readonly item: SolarProjectTechnology;
  readonly onSubmit: (data: SolarSubmitFields) => void;
};

export function SolarFormNew({ id, item, onSubmit }: SolarFormProps) {
  const { t } = useTranslation(['projects']);
  const { options: structure_typeOptions } = useStructureType();
  const { options: panel_configurationOptions } = usePanelConfiguration();
  const { options: developmentStatusOptions } = useDevelopmentStatus();

  const quarterOptions = [
    { label: 'Q1', value: '1' },
    { label: 'Q2', value: '2' },
    { label: 'Q3', value: '3' },
    { label: 'Q4', value: '4' },
  ];

  const options = useMemo(
    () => ({
      defaultValues: {
        development_status: item.development_status ?? null,
        installed_capacity_peak: item.installed_capacity_peak ?? null,
        installed_capacity_nominal: item.installed_capacity_nominal ?? null,
        specific_production: item.specific_production ?? null,
        ready_to_build: item.ready_to_build ?? null,
        ready_to_build_quarter: getQuarterFromDate(item.ready_to_build),
        ready_to_build_year: getYearFromDate(item.ready_to_build),
        commercial_operation_date_quarter: getQuarterFromDate(item.commercial_operation_date),
        commercial_operation_date_year: getYearFromDate(item.commercial_operation_date),
        commercial_operation_date: item.commercial_operation_date ?? null,
        comments: item.comments ?? null,
        structure_type: item?.structure_type ?? null,
        module_type: item?.module_type ?? null,
        module_power: item?.module_power ?? null,
        panel_configuration: item?.panel_configuration ?? null,
        pitch_distance: item?.pitch_distance ?? null,
      },
    }),
    [item]
  );

  const onSubmitGeneralForm = useCallback(
    (data: SolarFormFields) => {
      data.ready_to_build = getDateFromQuarterAndYear(data.ready_to_build_quarter, data.ready_to_build_year);
      data.commercial_operation_date = getDateFromQuarterAndYear(
        data.commercial_operation_date_quarter,
        data.commercial_operation_date_year
      );

      const payload = {
        development_status: data.development_status,
        installed_capacity_peak: data.installed_capacity_peak,
        installed_capacity_nominal: data.installed_capacity_nominal,
        ready_to_build: data.ready_to_build,
        commercial_operation_date: data.commercial_operation_date,
        specific_production: data.specific_production,
        comments: data.comments,
        structure_type: data.structure_type,
        module_type: data.module_type,
        module_power: data.module_power,
        panel_configuration: data.panel_configuration,
        pitch_distance: data.pitch_distance,
      };

      onSubmit(payload);
    },
    [onSubmit]
  );

  return (
    <Form<SolarFormFields> id={id} onSubmit={(data) => onSubmitGeneralForm(data)} options={options}>
      {({ formState, register }) => (
        <div className="space-y-6">
          <Typography as="p" color="secondary" size="body-3" weight="normal" className="mt-1 mb-10 italic">
            {t(
              'Please fill in this section with the main details about the project, including technology, development status, location, installed capacity, ready-to-build (RtB) and COD dates, etc.'
            )}
          </Typography>

          <Field
            id="development_status"
            label={t('Development status')}
            error={formState.errors['development_status']}
            delay={0}
          >
            <Select
              id="development_status"
              registration={register('development_status', { setValueAs: nullableStringSetter })}
              options={developmentStatusOptions}
              placeholder={t('Select a development status')}
              color={formState.errors['development_status'] ? 'error' : 'primary'}
            />
          </Field>

          <Field
            id="installed_capacity_peak"
            label={t('Installed Capacity Peak')}
            error={formState.errors['installed_capacity_peak']}
            delay={0}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="installed_capacity_peak"
              type="number"
              suffix="MWp"
              placeholder={t('Example: 300')}
              color={formState.errors['installed_capacity_peak'] ? 'error' : 'primary'}
              autoComplete="off"
              registration={register('installed_capacity_peak', {
                ...NUMERIC,
              })}
            />
          </Field>

          <Field
            id="installed_capacity_nominal"
            label={t('Installed Capacity Nominal')}
            error={formState.errors['installed_capacity_nominal']}
            delay={0.15}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="installed_capacity_nominal"
              type="number"
              suffix="MWn"
              placeholder={t('Example: 379')}
              color={formState.errors['installed_capacity_nominal'] ? 'error' : 'primary'}
              autoComplete="off"
              registration={register('installed_capacity_nominal', { ...NUMERIC })}
            />
          </Field>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <Field
              id="ready_to_build"
              label={t('Ready to Build Date')}
              error={formState.errors['ready_to_build_quarter'] || formState.errors['ready_to_build_year']}
              delay={0.25}
              classes={{ field: 'space-y-1' }}
            >
              <div className="flex gap-1">
                <Select
                  id="ready_to_build_quarter"
                  options={quarterOptions}
                  placeholder={t('Select a quarter')}
                  color={formState.errors['ready_to_build_quarter'] ? 'error' : 'primary'}
                  registration={register('ready_to_build_quarter')}
                />
                <Input
                  id="ready_to_build_year"
                  type="number"
                  step="1"
                  color={formState.errors['ready_to_build_year'] ? 'error' : 'primary'}
                  autoComplete="off"
                  registration={register('ready_to_build_year', { ...NUMERIC })}
                  placeholder={t('Year')}
                />
              </div>
            </Field>

            <Field
              id="commercial_operation_date"
              label={t('Commercial Operation Date')}
              error={
                formState.errors['commercial_operation_date_quarter'] ||
                formState.errors['commercial_operation_date_year']
              }
              delay={0.25}
              classes={{ field: 'space-y-1' }}
            >
              <div className="flex gap-1">
                <Select
                  id="commercial_operation_date_quarter"
                  options={quarterOptions}
                  placeholder={t('Select a quarter')}
                  color={formState.errors['commercial_operation_date_quarter'] ? 'error' : 'primary'}
                  registration={register('commercial_operation_date_quarter')}
                />
                <Input
                  id="commercial_operation_date_year"
                  type="number"
                  step="1"
                  color={formState.errors['commercial_operation_date_year'] ? 'error' : 'primary'}
                  autoComplete="off"
                  registration={register('commercial_operation_date_year', { ...NUMERIC })}
                  placeholder={t('Year')}
                />
              </div>
            </Field>
          </div>

          <Field
            id="specific_production"
            label={t('Specific production')}
            error={formState.errors['specific_production']}
            delay={0.45}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="specific_production"
              type="number"
              suffix="MWh/MWp"
              placeholder={t('Example: 379')}
              color={formState.errors['specific_production'] ? 'error' : 'primary'}
              autoComplete="off"
              registration={register('specific_production', { ...NUMERIC })}
            />
          </Field>

          <Field
            id="structure_type"
            label={t('Structure type')}
            error={formState.errors['structure_type']}
            delay={0}
            classes={{ field: 'space-y-1' }}
          >
            <Select
              id="structure_type"
              options={structure_typeOptions}
              placeholder={t('Select an option')}
              color={formState.errors['structure_type'] ? 'error' : 'primary'}
              registration={register('structure_type', { setValueAs: nullableStringSetter })}
            />
          </Field>

          <Field
            id="module_type"
            label={t('Module type')}
            error={formState.errors['module_type']}
            delay={0.15}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="module_type"
              type="text"
              color={formState.errors['module_type'] ? 'error' : 'primary'}
              registration={register('module_type', { setValueAs: nullableStringSetter })}
            />
          </Field>

          <Field
            id="module_power"
            label={t('Module power')}
            error={formState.errors['module_power']}
            delay={0.25}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="module_power"
              type="number"
              step="0.1"
              suffix="W"
              color={formState.errors['module_power'] ? 'error' : 'primary'}
              registration={register('module_power', { min: 0, ...NUMERIC })}
            />
          </Field>

          <Field
            id="panel_configuration"
            label={t('Panel configuration')}
            error={formState.errors['panel_configuration']}
            delay={0.35}
            classes={{ field: 'space-y-1' }}
          >
            <Select
              id="panel_configuration"
              options={panel_configurationOptions}
              placeholder={t('Select an option')}
              color={formState.errors['panel_configuration'] ? 'error' : 'primary'}
              registration={register('panel_configuration', { setValueAs: nullableStringSetter })}
            />
          </Field>

          <Field
            id="pitch_distance"
            label={t('Pitch distance')}
            error={formState.errors['pitch_distance']}
            delay={0.45}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="pitch_distance"
              type="number"
              suffix="m"
              color={formState.errors['pitch_distance'] ? 'error' : 'primary'}
              registration={register('pitch_distance', { min: 0, ...NUMERIC })}
            />
          </Field>

          <Field
            id="comments"
            label={t('Comments')}
            error={formState.errors['comments']}
            delay={0.45}
            classes={{ field: 'space-y-1' }}
          >
            <TextArea
              id="comments"
              color={formState.errors['comments'] ? 'error' : 'primary'}
              registration={register('comments', { setValueAs: nullableStringSetter })}
            />
          </Field>
        </div>
      )}
    </Form>
  );
}
